import { cn } from "@/lib/utils";
import { borderShapeClass } from "@/lib/utils/theme-configuration-class";
import { Media as MediaType, MediaField } from "@/payload-types";
import { Image } from "./image";
import { Video } from "./video";

interface MediaComponentProps {
	media: MediaField;
	className?: string;
	imageSize?: keyof NonNullable<MediaType["sizes"]>;
}

/**
 * Renders media content (image, video file, or video embed) based on the media field structure.
 * Handles wrap and border shape styling for both images and videos.
 *
 * @param props - Component props.
 * @returns The rendered media element or null if no valid media is provided.
 */
export const Media: React.FC<MediaComponentProps> = ({ media, className, imageSize }) => {
	const mediaType = media?.mediaType ?? "image";
	const wrapperClassName = cn(
		(media?.wrap || media?.borderShape) && `ms-media-wrap ms-media-wrap-${media?.wrap ?? "default"}`,
		(media?.wrap || media?.borderShape) && borderShapeClass(media?.borderShape),
		className,
	);

	if (mediaType === "image") {
		if (!media?.media) {
			return null;
		}
		return (
			<Image
				media={media.media}
				size={imageSize}
				className="w-full h-auto"
				wrapperClassName={wrapperClassName}
				priorityLoad={media.priorityLoad ?? false}
			/>
		);
	}

	if (mediaType === "video") {
		return (
			<Video
				sourceType={media?.sourceType ?? "file"}
				file={media?.file ?? undefined}
				embedUrl={media?.embedUrl ?? undefined}
				autoplay={media?.autoplay ?? false}
				wrapperClassName={wrapperClassName}
			/>
		);
	}

	return null;
};
